/**
 * Layout Nodes Utility
 *
 * Uses Dagre graph layout library to automatically position nodes
 * in a clean, hierarchical diagram.
 *
 * This prevents overlapping nodes and creates readable flow diagrams
 * without manual positioning.
 */

import dagre from '@dagrejs/dagre'
import { Node, Edge } from '@xyflow/react'

/**
 * Layout direction options
 * - TB: Top to Bottom (vertical, best for mobile)
 * - LR: Left to Right (horizontal, best for desktop)
 * - RL: Right to Left (reverse horizontal)
 * - BT: Bottom to Top (reverse vertical)
 */
export type LayoutDirection = 'TB' | 'LR' | 'RL' | 'BT'

/**
 * Configuration options for layout algorithm
 */
export interface LayoutOptions {
  /** Direction of graph flow */
  direction: LayoutDirection
  /** Width of each node (pixels) */
  nodeWidth?: number
  /** Height of each node (pixels) */
  nodeHeight?: number
  /** Spacing between levels/ranks (pixels) */
  rankSep?: number
  /** Spacing between nodes in same rank (pixels) */
  nodeSep?: number
}

/**
 * The px value used for each option the caller leaves out, one at a time.
 * Every node is laid out as nodeWidth by nodeHeight; no rendered size is read.
 * SeriesGraphEmbedPage passes TribeMoveNode's NODE_WIDTH and NODE_HEIGHT.
 * SeriesDetailPage passes only direction, so its 280px-wide MoveNode is laid
 * out at the 200px default width.
 */
const DEFAULT_OPTIONS: Required<Omit<LayoutOptions, 'direction'>> = {
  nodeWidth: 200,
  nodeHeight: 180,
  rankSep: 150,
  nodeSep: 100,
}

/**
 * Calculate optimal positions for nodes using Dagre layout algorithm
 *
 * @param nodes - Array of React Flow nodes
 * @param edges - Array of React Flow edges (connections)
 * @param options - Layout configuration
 * @returns Array of nodes with calculated positions
 *
 * @example
 * ```ts
 * // Desktop layout (left-to-right)
 * const layouted = layoutNodes(nodes, edges, { direction: 'LR' });
 *
 * // Mobile layout (top-to-bottom)
 * const layouted = layoutNodes(nodes, edges, { direction: 'TB' });
 *
 * // Custom spacing
 * const layouted = layoutNodes(nodes, edges, {
 *   direction: 'LR',
 *   rankSep: 200,
 *   nodeSep: 120
 * });
 * ```
 */
export function layoutNodes<NodeType extends Node = Node>(
  nodes: NodeType[],
  edges: Edge[],
  options: LayoutOptions
): NodeType[] {
  // Merge with defaults
  const {
    direction,
    nodeWidth = DEFAULT_OPTIONS.nodeWidth,
    nodeHeight = DEFAULT_OPTIONS.nodeHeight,
    rankSep = DEFAULT_OPTIONS.rankSep,
    nodeSep = DEFAULT_OPTIONS.nodeSep,
  } = options

  // Create a new directed graph
  const graph = new dagre.graphlib.Graph()

  // Set graph configuration
  graph.setGraph({
    rankdir: direction, // Direction of flow
    ranksep: rankSep, // Space between ranks
    nodesep: nodeSep, // Space between nodes
    marginx: 20, // Horizontal margin
    marginy: 20, // Vertical margin
  })

  // Default edge settings (no label)
  graph.setDefaultEdgeLabel(() => ({}))

  // Add nodes to graph with dimensions
  nodes.forEach(node => {
    graph.setNode(node.id, {
      width: nodeWidth,
      height: nodeHeight,
    })
  })

  // Add edges to graph (defines relationships)
  edges.forEach(edge => {
    graph.setEdge(edge.source, edge.target)
  })

  // Run the layout algorithm
  // This calculates optimal x,y positions for each node
  dagre.layout(graph)

  // Apply calculated positions to nodes
  const layoutedNodes = nodes.map(node => {
    const position = graph.node(node.id)

    // Position is calculated at center of node
    // We need to adjust to top-left corner for React Flow
    return {
      ...node,
      position: {
        x: position.x - nodeWidth / 2,
        y: position.y - nodeHeight / 2,
      },
    }
  })

  return layoutedNodes
}

/**
 * Get recommended layout direction based on screen size
 *
 * @param width - Viewport width in pixels
 * @returns Optimal layout direction
 *
 * @example
 * ```ts
 * const direction = getLayoutDirection(window.innerWidth);
 * const layouted = layoutNodes(nodes, edges, { direction });
 * ```
 */
export function getLayoutDirection(width: number): LayoutDirection {
  // Mobile: vertical layout
  // Desktop: horizontal layout
  return width < 768 ? 'TB' : 'LR'
}

/**
 * Re-layout nodes while preserving custom node data
 *
 * Useful when nodes have been manually dragged but you want to
 * reset them to automatic layout.
 *
 * @param nodes - Current nodes (possibly with custom positions)
 * @param edges - Current edges
 * @param options - Layout options
 * @returns Nodes with recalculated positions
 */
export function relayoutNodes<NodeType extends Node = Node>(
  nodes: NodeType[],
  edges: Edge[],
  options: LayoutOptions
): NodeType[] {
  // Simply run layout again
  // This will override any manual positioning
  return layoutNodes(nodes, edges, options)
}

/**
 * Check if nodes need re-layout after window resize
 *
 * @param previousWidth - Previous window width
 * @param currentWidth - Current window width
 * @returns True if layout direction would change
 *
 * @example
 * ```ts
 * useEffect(() => {
 *   const handleResize = () => {
 *     if (shouldRelayout(previousWidth, window.innerWidth)) {
 *       const direction = getLayoutDirection(window.innerWidth);
 *       const layouted = relayoutNodes(nodes, edges, { direction });
 *       setNodes(layouted);
 *     }
 *   };
 *   window.addEventListener('resize', handleResize);
 *   return () => window.removeEventListener('resize', handleResize);
 * }, []);
 * ```
 */
export function shouldRelayout(
  previousWidth: number,
  currentWidth: number
): boolean {
  const previousDirection = getLayoutDirection(previousWidth)
  const currentDirection = getLayoutDirection(currentWidth)

  // Only re-layout if direction changes (mobile ↔ desktop)
  return previousDirection !== currentDirection
}
